import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Router } from './router';
import { AppShell } from './app-shell';
import { HotkeysProvider } from 'react-hotkeys-hook';

import 'react-modern-drawer/dist/index.css';

/**
 * Application-wide React Query client.
 *
 * Only overrides of React Query's own defaults are set here; everything else
 * (staleTime 0, gcTime 5min, mutation retry 0) is left at the library default.
 * - `retry: 0` — surface errors explicitly rather than silently retrying 3×,
 *   especially while endpoints are MSW-mocked. Per-hook overridable.
 * - `refetchOnWindowFocus: false` — not useful for read-only paginated tables.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false,
    },
  },
});

export function App({ csrfToken }: { csrfToken: string }) {
  // NOTE: <StrictMode> is temporarily removed for the React 18 cutover, per the
  // official upgrade guide ("Remove Strict Mode during the upgrade, then add it
  // back after you fix the issues it points out"). Re-enable once the effect
  // ref-guards are hardened for React 18's dev double-invoke — tracked in the
  // frontend-upgrade deferred ledger.
  return (
    <QueryClientProvider client={queryClient}>
      <HotkeysProvider initiallyActiveScopes={[]}>
        <AppShell Router={Router} csrfToken={csrfToken} />
      </HotkeysProvider>
      {/*
        ReactQueryDevtools self-excludes from production: its published entry
        renders null unless process.env.NODE_ENV === 'development', and Vite
        statically replaces NODE_ENV at build time so it tree-shakes out of
        prod bundles. No manual gating needed (checked in the v5 package).
      */}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
